import React, { useRef } from 'react';
import { Transition, forceReflow } from './Transition';
import type { CollapseProps } from './Transition.types';
import clsx from 'clsx';

export const Collapse = ({
  in: inProp,
  className,
  children,
  timeout = 300,
}: CollapseProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  /**
   * The collapse styles are based on the state of the collapse. The state is determined by the Transition component.
   */
  const collapseStyles = {
    exited: 'collapse-exited',
    exiting: 'collapse-exiting',
    entered: 'collapse-entered',
    entering: 'collapse-entering',
  };

  /**
   * This function is called when the collapse is first entered. It sets the height of the wrapper to null.
   */
  const onEnter = () => {
    if (wrapperRef.current) {
      wrapperRef.current.style.height = '0';
    }
  };

  /**
   * This function is called when the collapse is entering. It sets the height of the wrapper to the scroll height.
   */
  const onEntering = () => {
    if (wrapperRef.current) {
      wrapperRef.current.style.height = `${wrapperRef.current.scrollHeight}px`;
    }
  };

  /**
   * This function is called when the collapse is entered. It sets the height of the wrapper to null.
   */
  const onEntered = () => {
    if (wrapperRef.current) {
      wrapperRef.current.style.height = null;
    }
  };

  /**
   * This function is called when the collapse is first exited. It sets the height of the wrapper to the current height.
   */
  const onExit = () => {
    if (wrapperRef.current) {
      wrapperRef.current.style.height = `${wrapperRef.current.scrollHeight}px`;
      forceReflow(wrapperRef.current); // force reflow
    }
  };

  /**
   * This function is called when the collapse is exiting. It sets the height of the wrapper to 0.
   */
  const onExiting = () => {
    if (wrapperRef.current) {
      wrapperRef.current.style.height = null;
    }
  };

  return (
    <Transition
      in={inProp}
      nodeRef={wrapperRef}
      timeout={timeout}
      onEnter={onEnter}
      onEntered={onEntered}
      onEntering={onEntering}
      onExit={onExit}
      onExiting={onExiting}
    >
      {(state) => (
        <div
          ref={wrapperRef}
          className={clsx(className, collapseStyles[state])}
        >
          {children}
        </div>
      )}
    </Transition>
  );
};
